/**
 * useBulkSelection.js — small hook for sticky multi-select UIs (e.g. SKU list
 * bulk print, batch operations).
 *
 * Owns a `Set` of selected ids and exposes the handlers that list rows and
 * action bars need. Extracted from Inventory.jsx (iter 102).
 *
 * Usage:
 *   const sel = useBulkSelection();
 *   sel.toggle(id); sel.isSelected(id); sel.clear();
 *   sel.selectAll(items, (it) => it.id);
 *   sel.isAllSelected(items, (it) => it.id);
 *   sel.count; sel.ids; // array
 */
import { useCallback, useMemo, useState } from 'react';

export default function useBulkSelection() {
  const [ids, setIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    setIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setIds(new Set()), []);

  const selectAll = useCallback((items, getId = (x) => x.id) => {
    setIds((prev) => {
      const next = new Set(prev);
      items.forEach((it) => next.add(getId(it)));
      return next;
    });
  }, []);

  const unselectAll = useCallback((items, getId = (x) => x.id) => {
    setIds((prev) => {
      const next = new Set(prev);
      items.forEach((it) => next.delete(getId(it)));
      return next;
    });
  }, []);

  const isSelected = useCallback((id) => ids.has(id), [ids]);

  const isAllSelected = useCallback((items, getId = (x) => x.id) => {
    if (items.length === 0) return false;
    return items.every((it) => ids.has(getId(it)));
  }, [ids]);

  // Stable toggle for the "header" checkbox that toggles the whole currently-
  // filtered list. Callers pass in the current visible list.
  const toggleAll = useCallback((items, getId = (x) => x.id) => {
    const allSelected = items.length > 0 && items.every((it) => ids.has(getId(it)));
    if (allSelected) unselectAll(items, getId); else selectAll(items, getId);
  }, [ids, selectAll, unselectAll]);

  return useMemo(() => ({
    ids: Array.from(ids),
    idSet: ids,
    count: ids.size,
    toggle,
    clear,
    selectAll,
    unselectAll,
    isSelected,
    isAllSelected,
    toggleAll,
  }), [ids, toggle, clear, selectAll, unselectAll, isSelected, isAllSelected, toggleAll]);
}
